/**
 * Forzarocket homepage behaviour.
 *
 * - the 4-Pocket / 9-Pocket segmented switch above the colour grid
 * - the FAQ accordion (one row open at a time)
 * - the virtual binder pop-up opened from the formats footnote
 *
 * Both are attribute-driven: the CSS in sections/forza-home.liquid reacts to
 * `data-fzh-active` and `aria-expanded`, so this file only flips state.
 */

const init = (root = document) => {
  root.querySelectorAll('[data-fzh-switch]').forEach((sw) => {
    if (sw.dataset.fzhBound === 'true') return;
    sw.dataset.fzhBound = 'true';

    const buttons = [...sw.querySelectorAll('[data-fzh-format]')];
    const scope = sw.closest('.fzh-colours') ?? root;

    sw.addEventListener('click', (event) => {
      const button = event.target.closest('[data-fzh-format]');
      if (!button || !buttons.includes(button)) return;

      const format = button.dataset.fzhFormat;
      if (sw.dataset.fzhActive === format) return;

      sw.dataset.fzhActive = format;

      buttons.forEach((candidate) => {
        const active = candidate === button;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-pressed', String(active));
      });

      scope.querySelectorAll('[data-fzh-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.fzhPanel !== format;
      });
    });
  });

  root.querySelectorAll('[data-fzh-faq]').forEach((list) => {
    if (list.dataset.fzhBound === 'true') return;
    list.dataset.fzhBound = 'true';

    list.addEventListener('click', (event) => {
      const trigger = event.target.closest('.fzh-faq__q');
      if (!trigger || !list.contains(trigger)) return;

      const wasOpen = trigger.getAttribute('aria-expanded') === 'true';

      list.querySelectorAll('.fzh-faq__q').forEach((other) => {
        other.setAttribute('aria-expanded', 'false');
      });

      trigger.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
    });
  });

  root.querySelectorAll('[data-fzh-binder-modal]').forEach((modal) => {
    if (modal.dataset.fzhBound === 'true') return;
    modal.dataset.fzhBound = 'true';

    const scope = modal.closest('.fzh') ?? document;
    let lastFocused = null;

    const open = () => {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('fzh-binder-open');
      modal.querySelector('[data-fzh-binder-close]')?.focus();
    };

    const close = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      document.documentElement.classList.remove('fzh-binder-open');
      lastFocused?.focus?.();
    };

    scope.querySelectorAll('[data-fzh-binder-open]').forEach((opener) => {
      opener.addEventListener('click', open);
    });

    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-fzh-binder-close]')) close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || modal.hidden) return;
      // The binder's card picker handles Escape itself; close it first.
      if (modal.querySelector('.frz-cardpicker--open')) return;
      close();
    });
  });
};

init();

// Re-bind after a theme-editor section render, which replaces the markup.
document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});
